import React, { useEffect, useState } from 'react';
import UserDashboardShell from '../../components/user/UserDashboardShell';
import { useAuth } from '../../auth/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { splitUserName } from '../../utils/userName';

const buildSettingsForm = (user) => {
  const { firstName, lastName } = splitUserName(user);

  return {
    firstName,
    lastName,
    phone: user?.phone || '',
    email: user?.email || '',
    password: '',
  };
};

const SETTINGS_FIELDS = [
  {
    key: 'firstName',
    labelKey: 'settings.name',
    placeholderKey: 'settings.firstNamePlaceholder',
    type: 'text',
  },
  {
    key: 'lastName',
    labelKey: 'settings.surname',
    placeholderKey: 'settings.surnamePlaceholder',
    type: 'text',
  },
  {
    key: 'phone',
    labelKey: 'settings.mobile',
    placeholderKey: 'settings.mobilePlaceholder',
    type: 'tel',
  },
  {
    key: 'email',
    labelKey: 'settings.email',
    placeholderKey: 'settings.emailPlaceholder',
    type: 'email',
  },
  {
    key: 'password',
    labelKey: 'settings.password',
    placeholderKey: 'settings.passwordPlaceholder',
    type: 'password',
  },
];

const Settings = () => {
  const { user, updateProfile } = useAuth();
  const { t } = useLanguage();
  const [formData, setFormData] = useState(buildSettingsForm(user));
  const [editingFields, setEditingFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    setFormData(buildSettingsForm(user));
  }, [user]);

  const handleToggleField = (fieldKey) => {
    setEditingFields((currentFields) => ({
      ...currentFields,
      [fieldKey]: !currentFields[fieldKey],
    }));
  };

  const handleFieldChange = (fieldKey, value) => {
    setFormData((currentData) => ({
      ...currentData,
      [fieldKey]: value,
    }));
  };

  const getDisplayValue = (fieldKey) => {
    if (fieldKey === 'password') {
      return formData.password ? '*'.repeat(Math.max(formData.password.length, 10)) : '************';
    }

    return formData[fieldKey] || t('common.notSet');
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFeedback(null);

    try {
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
      };

      if (formData.password.trim()) {
        payload.password = formData.password.trim();
      }

      await updateProfile(payload);
      setFormData((currentData) => ({ ...currentData, password: '' }));
      setEditingFields({});
      setFeedback({
        type: 'success',
        text: t('settings.saved'),
      });
    } catch (error) {
      console.error('Failed to save account settings:', error);
      setFeedback({
        type: 'error',
        text: error?.response?.data?.message || t('settings.failed'),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <UserDashboardShell>
      <section className="account-page">
        <h2 className="account-page__title">{t('settings.title')}</h2>

        <form className="account-settings__form" onSubmit={handleSave}>
          {SETTINGS_FIELDS.map((field) => {
            const isEditing = !!editingFields[field.key];

            return (
              <div key={field.key} className="account-settings__row">
                <div>
                  <h3 className="account-settings__label">{t(field.labelKey)}</h3>
                  {isEditing ? (
                    <input
                      className="account-settings__input"
                      type={field.type}
                      value={formData[field.key]}
                      placeholder={t(field.placeholderKey)}
                      autoComplete={field.key === 'password' ? 'new-password' : 'on'}
                      onChange={(event) => handleFieldChange(field.key, event.target.value)}
                    />
                  ) : (
                    <div className="account-settings__value">{getDisplayValue(field.key)}</div>
                  )}
                </div>

                <button type="button" className="account-settings__action" onClick={() => handleToggleField(field.key)}>
                  {isEditing ? t('common.done') : t('common.change')}
                </button>
              </div>
            );
          })}

          <div className="account-settings__footer">
            <button type="submit" className="account-button account-button--outline" disabled={saving}>
              {saving ? t('common.saving') : t('common.saveChanges')}
            </button>

            {feedback ? (
              <span className={`account-settings__message ${feedback.type === 'success' ? 'is-success' : 'is-error'}`}>
                {feedback.text}
              </span>
            ) : null}
          </div>
        </form>
      </section>
    </UserDashboardShell>
  );
};

export default Settings;
